import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { LoginCredentials, SignupData } from '../types/auth';
import { api } from '../services/api';
import { User } from '../types/user';
import { anonymousTestHandler } from '../services/anonymousTestHandler';

export function useAuth() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { 
    login: storeLogin, 
    logout: storeLogout,
    user,
    isAuthenticated
  } = useAuthStore();

  // Debug auth state on mount
  useEffect(() => {
    console.log('Auth state in useAuth hook:', useAuthStore.getState());
  }, []);

  const handleNavigation = useCallback((user: User) => {
    console.log('Navigating based on user role and test status:', user.role, user.enneagramType, user.hasCompletedTest);
    if (user.role === 'admin') {
      console.log('User is admin, directing to admin dashboard');
      navigate('/admin');
      return;
    } else if (user.hasCompletedTest || (user.enneagramType !== undefined && user.enneagramType !== null)) {
      // If user has completed the test or has an enneagram type, direct to dashboard
      console.log('User has completed a test, directing to dashboard');
      navigate('/dashboard');
    } else {
      // If user hasn't taken a test, direct to test page
      console.log('User has not completed a test, directing to test page');
      navigate('/test');
    }
  }, [navigate]);

  // Check and attempt to claim anonymous test results
  const checkAndClaimAnonymousResults = useCallback(async (userId: string) => {
    if (anonymousTestHandler.hasStoredResults()) {
      console.log('Found anonymous test results to claim for user:', userId);
      try {
        const success = await anonymousTestHandler.claimTestResults(userId);
        if (success) {
          console.log('Successfully claimed anonymous test results');
          // Update user information after claiming
          return true;
        } else {
          console.warn('Failed to claim anonymous test results');
        }
      } catch (error) {
        console.error('Error claiming anonymous test results:', error);
      }
    } else {
      console.log('No anonymous test results to claim');
    }
    return false;
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    setError(null);

    try {
      // Mock login for development
      if (import.meta.env.DEV) {
        console.log('Using mock login in development mode');
        const mockUsers = [
          { 
            email: 'admin@test.com', 
            password: 'admin123', 
            role: 'admin',
            enneagramType: 1
          },
          { 
            email: 'test@test.com', 
            password: 'test123', 
            role: 'user',
            enneagramType: null
          },
          { 
            email: 'user@test.com', 
            password: 'user123', 
            role: 'user',
            enneagramType: 2
          },
          {
            email: 'admin2@example.com',
            password: 'Admin123!',
            role: 'admin',
            enneagramType: null
          }
        ];

        const mockUser = mockUsers.find(u => 
          u.email === credentials.email && u.password === credentials.password
        );

        if (mockUser) {
          const user: User = {
            id: '507f1f77bcf86cd799439011',
            email: mockUser.email,
            role: mockUser.role as 'admin' | 'user',
            name: 'Test User',
            phone: '555-123-4567',
            enneagramType: mockUser.enneagramType
          };
          
          storeLogin(user, 'mock-token');
          console.log('Mock login successful, navigating with user:', user);
          
          // For admin users, ensure hasCompletedTest is set to avoid any redirection issues
          if (user.role === 'admin') {
            console.log('Setting up admin user properties');
            user.hasCompletedTest = true;
          }
          // Check for anonymous test results
          else if (anonymousTestHandler.hasStoredResults()) {
            console.log('Found anonymous test results in mock login');
            // In dev mode, just clear the stored results without claiming
            anonymousTestHandler.clearStoredResults();
            user.hasCompletedTest = true;
            user.enneagramType = 1; // Mock enneagram type
          }
          
          // IMPORTANT: Navigate based on user role - direct admins to admin dashboard
          handleNavigation(user);
          return;
        }
      }

      // Real API login
      console.log('Attempting real API login with:', credentials.email);
      const response = await api.auth.login(credentials);
      console.log('Login response received:', response);
      
      // Make sure we have a user with a role
      const user: User = { ...response.user };
      if (!user.role) {
        console.warn('User role is missing in response, defaulting to "user"');
        user.role = 'user';
      }
      
      storeLogin(user, response.token);
      console.log('User authenticated with role:', user.role);
      
      // For admin users, ensure we set hasCompletedTest to avoid any redirection issues
      if (user.role === 'admin') {
        console.log('Setting up admin user properties after API login');
        user.hasCompletedTest = true;
      }
      
      // Check for anonymous test results and claim them if found
      const claimed = await checkAndClaimAnonymousResults(user.id);
      if (claimed) {
        // Refresh user data after claiming
        try {
          const updatedUser = await api.auth.getCurrentUser();
          if (updatedUser) {
            // Update user in store
            storeLogin({ ...updatedUser, id: user.id }, response.token);
            console.log('Updated user after claiming test:', updatedUser);
          }
        } catch (refreshError) {
          console.error('Error refreshing user after claiming test:', refreshError);
        }
      }
      
      // Add a small delay to ensure state updates before navigation
      setTimeout(() => {
        handleNavigation(user);
      }, 100);
    } catch (error) {
      console.error('Login error:', error);
      setError('Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, storeLogin, handleNavigation, checkAndClaimAnonymousResults]);

  const signup = useCallback(async (credentials: SignupData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.auth.signup(credentials);
      const user: User = { ...response.user };
      storeLogin(user, response.token);
      
      // Check for anonymous test results and claim them if found
      await checkAndClaimAnonymousResults(user.id);
      
      handleNavigation(user);
    } catch (error) {
      console.error('Signup error:', error);
      setError('Error creating account');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, storeLogin, handleNavigation, checkAndClaimAnonymousResults]);

  const logout = useCallback(async () => {
    await storeLogout();
    navigate('/login');
  }, [navigate, storeLogout]);

  return {
    login,
    signup,
    logout,
    isLoading,
    error,
    user,
    isAuthenticated
  };
}